import { Avatar, Stack, Typography } from '@mui/material';
import React from 'react';

import Icon from '@components/Icon/Icon';

import { isProviderSlug } from '@utils/device-brand';
import { getImageSrc } from '@utils/image-handler';

/** Tile artwork is square; the chip scales it down rather than cropping it. */
const TILE_SIZE = 20;

export interface DeviceBrandChipProps {
  /** Brand display name, already resolved from the provider slug. */
  name: string;
  /**
   * Terra provider slug, used to find the app tile. Omit it and the chip is
   * text only, which is what non-device sources get.
   */
  slug?: string;
}

/**
 * A device brand, as a tile and its name.
 *
 * The tile is a rounded square. Garmin's guidelines treat cropping their app
 * tile to a circle as restyling it, so this must not become an `Avatar` with
 * the default circular variant.
 *
 * A slug we have no artwork for falls back to the device glyph rather than
 * `Avatar`'s default person silhouette. Anything that is not a provider slug
 * gets no tile at all: callers pass `ObservationRecord.source`, which falls
 * back to a practitioner name, and that would build a nonsense tile key.
 */
export default function DeviceBrandChip({ name, slug }: DeviceBrandChipProps) {
  const isDevice = isProviderSlug(slug);
  const tile = isDevice
    ? getImageSrc(`public/terra/brands/${slug}.webp`, TILE_SIZE, TILE_SIZE)
    : null;

  return (
    <Stack
      alignItems="center"
      direction="row"
      spacing={0.75}
      sx={({ palette }) => ({
        backgroundColor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        borderRadius: 999,
        px: 1.5,
        py: 0.625,
      })}
    >
      {isDevice ? (
        <Avatar
          alt=""
          src={tile ?? undefined}
          sx={{ height: TILE_SIZE, width: TILE_SIZE }}
          variant="rounded"
        >
          <Icon name="watch-fitness" size="xs" />
        </Avatar>
      ) : null}
      <Typography variant="subtitle2" color="text.primary">
        {name}
      </Typography>
    </Stack>
  );
}
